import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Typography, Form, DatePicker, Select, Button, Space, Table, Tag, Tabs, Drawer, InputNumber, Tooltip, Card, Empty, Row, Col,
} from 'antd';
import { ArrowDownOutlined, ArrowUpOutlined } from '@ant-design/icons';
import { useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';
import api from '../api';

import { selectFilter } from '../utils/selectFilter';
// Error Insights: where to look, rather than every number. The error side of
// Eval Insights; errors have no score, so it points at counts instead.
//   Top Error Areas  sections and questions with the most errors, errors per
//                    100 calls, change vs the previous period, and whether a
//                    few agents make most of them (Concentrated: individual
//                    coaching) or many do (Widespread: training, process or
//                    the form)
//   Repeat Errors    agent x section grid, red at the repeat threshold
//                    (default 4: the 3rd error raises coaching, so the 4th is
//                    the repeat); repeats after coaching on that section; and
//                    repeats with no coaching on that section at all
//   Agent Check-up   one agent's sections and questions against everyone on
//                    the same filters, with the coaching on each section and a
//                    month trend
// Errors and feedbacks come from /api/errors/question-stats, the feed behind
// Error Agent Stats' Section / Question Stats, so the numbers agree.
// Feedbacks are their own column and never counted as errors. Per 100 calls
// uses calls handled on the campaigns from AgentMonthlyStats (CXone and AWS
// alike), part months counted by their share of days; where there are none
// (chat, email) the rate shows as a dash.

const { RangePicker } = DatePicker;
const byNum = (k) => (a, b) => (a[k] ?? -1) - (b[k] ?? -1);
const per100 = (errors, calls) => (calls > 0 ? Math.round((errors / calls) * 10000) / 100 : null);
const dash = <span style={{ color: '#bfbfbf' }}>—</span>;

// Sections or questions from the agent x question rows: errors, feedbacks and
// each agent's errors on it.
function sumUp(rows, keyOf) {
  const m = {};
  rows.forEach((r) => {
    const k = keyOf(r);
    const g = (m[k] ||= { key: k, SectionName: r.SectionName, QuestionText: r.QuestionText, Errors: 0, Feedbacks: 0, byAgent: {} });
    g.Errors    += r.TotalErrors || 0;
    g.Feedbacks += r.TotalFeedbacks || 0;
    const ak = r.AgentUserID ?? `name:${r.AgentName}`;
    const a = (g.byAgent[ak] ||= { AgentUserID: r.AgentUserID, AgentName: r.AgentName, Errors: 0, Feedbacks: 0 });
    a.Errors    += r.TotalErrors || 0;
    a.Feedbacks += r.TotalFeedbacks || 0;
  });
  return Object.values(m).map((g) => ({ ...g, agents: Object.values(g.byAgent) }));
}

/**
 * `embedded` (Campaign Summary > Errors > Insights) renders this page scoped
 * to the campaign and date window passed in: no heading, no Date of Error or
 * Campaign filters (Error Form stays), and the Agent Check-up agent is kept
 * on the page rather than in the address, which is the Campaign Summary's.
 */
export default function ErrorInsightsPage({ embedded = false, campaignId = null, from = null, to = null }) {
  const [urlParams, setUrlParams] = useSearchParams();
  const params = embedded ? new URLSearchParams() : urlParams;
  const setParams = (p) => { if (!embedded) setUrlParams(p); };
  const [form] = Form.useForm();

  const [forms, setForms]         = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [threshold, setThreshold] = useState(4);

  const [qRows, setQRows]       = useState([]);
  const [prevQ, setPrevQ]       = useState([]);
  const [calls, setCalls]       = useState({ agents: {}, total_calls: 0, has_calls: false });
  const [coaching, setCoaching] = useState([]);
  const [loading, setLoading]   = useState(false);
  const [tab, setTab]           = useState(params.get('agent') ? 'agent' : 'top');
  const [agentId, setAgentId]   = useState(params.get('agent') ? Number(params.get('agent')) : null);
  const [trend, setTrend]       = useState([]);
  const [panel, setPanel]       = useState(null);    // { title, kind, rows, loading }

  useEffect(() => {
    api.get('/api/errors/filter-options').then((r) => {
      setCampaigns((r.data.campaigns || []).map((c) => ({ label: c.Name, value: c.ID })));
      setForms((r.data.forms || []).map((f) => ({ label: f.name, value: f.name })));
    }).catch(() => {});
  }, []);

  const buildParams = (range) => {
    const v = form.getFieldsValue();
    const p = {};
    if (range?.[0]) p.start_date = range[0].format('YYYY-MM-DD');
    if (range?.[1]) p.end_date   = range[1].format('YYYY-MM-DD');
    if (v.campaigns?.length) p.campaigns = v.campaigns.join(',');
    if (v.form_name) p.form_name = v.form_name;
    return p;
  };

  const load = useCallback(async () => {
    const range = form.getFieldValue('range');
    setLoading(true);
    try {
      const cur = buildParams(range);
      // The previous period of the same length, for the change column.
      let prevP = null;
      if (range?.[0] && range?.[1]) {
        const days = range[1].diff(range[0], 'day') + 1;
        prevP = buildParams([range[0].subtract(days, 'day'), range[0].subtract(1, 'day')]);
      }
      const none = { data: [] };
      const [q, pq, c, co] = await Promise.all([
        api.get('/api/errors/question-stats', { params: { ...cur, group_by: 'summary' } }).catch(() => none),
        prevP ? api.get('/api/errors/question-stats', { params: { ...prevP, group_by: 'summary' } }).catch(() => none) : none,
        api.get('/api/error-insights/calls', { params: cur }).catch(() => ({ data: null })),
        api.get('/api/error-insights/coaching', { params: cur }).catch(() => none),
      ]);
      setQRows(q.data || []); setPrevQ(pq.data || []);
      setCalls(c.data || { agents: {}, total_calls: 0, has_calls: false });
      setCoaching(co.data || []);
    } finally {
      setLoading(false);
    }
  }, [form]); // eslint-disable-line react-hooks/exhaustive-deps

  // Embedded: the Campaign Summary's campaign and window, and follow them.
  const scopeValues = () => ({
    range: from && to ? [dayjs(from), dayjs(to)] : [dayjs().startOf('month'), dayjs()],
    campaigns: campaignId ? [campaignId] : [],
  });
  // On its own the page waits for a campaign before searching; embedded, the
  // campaign is already chosen.
  useEffect(() => {
    form.setFieldsValue(embedded ? scopeValues() : { range: [dayjs().startOf('month'), dayjs()] });
    if (embedded) load();
  }, []); // eslint-disable-line

  const firstScope = useRef(true);
  useEffect(() => {
    if (!embedded) return;
    if (firstScope.current) { firstScope.current = false; return; }
    form.setFieldsValue(scopeValues());
    load();
  }, [embedded, campaignId, from, to]); // eslint-disable-line

  // -- figures ---------------------------------------------------------------
  const hasCalls = !!calls.has_calls;
  const callsOf = (uid) => (uid != null ? (calls.agents?.[uid] ?? calls.agents?.[String(uid)] ?? 0) : 0);
  // Everyone's rate: errors of the agents with calls over all calls, so an
  // agent with errors but no mapped calls does not inflate it.
  const groupRate = (g) => (hasCalls
    ? per100(g.agents.reduce((s, a) => s + (callsOf(a.AgentUserID) > 0 ? a.Errors : 0), 0), calls.total_calls) : null);

  const sections  = useMemo(() => sumUp(qRows, (r) => r.SectionName), [qRows]);
  const questions = useMemo(() => sumUp(qRows, (r) => `${r.SectionName}||${r.QuestionText}`), [qRows]);
  const prevSecErr = useMemo(() => Object.fromEntries(sumUp(prevQ, (r) => r.SectionName).map((g) => [g.key, g.Errors])), [prevQ]);
  const prevQErr   = useMemo(() => Object.fromEntries(sumUp(prevQ, (r) => `${r.SectionName}||${r.QuestionText}`).map((g) => [g.key, g.Errors])), [prevQ]);
  const totalErrors = sections.reduce((s, g) => s + g.Errors, 0);
  const agentsWithErrors = new Set(qRows.filter((r) => (r.TotalErrors || 0) > 0).map((r) => r.AgentUserID ?? r.AgentName)).size;

  // Concentrated: the top fifth of the agents with errors on it (at least
  // one) make 60% or more of them.
  const spread = (g) => {
    const errs = g.agents.map((a) => a.Errors).filter((n) => n > 0).sort((a, b) => b - a);
    if (!errs.length) return null;
    const top = Math.max(1, Math.ceil(errs.length * 0.2));
    const share = errs.slice(0, top).reduce((s, n) => s + n, 0) / g.Errors;
    return { agents: errs.length, top, share, concentrated: share >= 0.6 };
  };

  const decorate = (g, prev) => ({
    ...g, Rate: groupRate(g), Share: totalErrors ? g.Errors / totalErrors : null,
    Prev: prev[g.key] ?? 0, Spread: spread(g),
  });
  const topSections  = sections.filter((g) => g.Errors > 0).map((g) => decorate(g, prevSecErr)).sort(byNum('Errors')).reverse();
  const topQuestions = questions.filter((g) => g.Errors > 0).map((g) => decorate(g, prevQErr)).sort(byNum('Errors')).reverse();

  const agentOptions = useMemo(() => {
    const m = {};
    qRows.forEach((r) => { if (r.AgentUserID) m[r.AgentUserID] = r.AgentName; });
    return Object.entries(m).map(([id, name]) => ({ value: Number(id), label: name })).sort((a, b) => a.label.localeCompare(b.label));
  }, [qRows]);

  // -- shared bits -------------------------------------------------------------
  // More errors is worse: up is red.
  const change = (cur, prev) => {
    const d = cur - prev;
    if (d === 0) return <span style={{ color: '#8c8c8c' }}>0</span>;
    return d > 0
      ? <span style={{ color: '#ff4d4f' }}><ArrowUpOutlined /> {d}</span>
      : <span style={{ color: '#52c41a' }}><ArrowDownOutlined /> {Math.abs(d)}</span>;
  };
  const rateCell = (v) => (v == null ? dash : v.toFixed(2));
  const spreadTag = (s) => {
    if (!s) return dash;
    const tip = `${s.agents} agent${s.agents === 1 ? '' : 's'}; top ${s.top}: ${Math.round(s.share * 100)}% of the errors`;
    return (
      <Tooltip title={tip}>
        <Tag color={s.concentrated ? 'orange' : 'blue'} style={{ marginRight: 0 }}>
          {s.concentrated ? 'Concentrated' : 'Widespread'}
        </Tag>
      </Tooltip>
    );
  };
  const countTag = (n, color = 'red') => (n > 0 ? <Tag color={color} style={{ marginRight: 0 }}>{n}</Tag> : dash);

  const openCheckup = (id) => {
    setAgentId(id);
    setTab('agent');
    setPanel(null);
    setParams({ agent: String(id) });
  };

  // Agents on a section or question, most errors first.
  const openAgents = (title, g) => setPanel({
    title, kind: 'agents',
    rows: g.agents.map((a) => ({ ...a, Rate: hasCalls ? per100(a.Errors, callsOf(a.AgentUserID)) : null }))
      .sort((a, b) => b.Errors - a.Errors || b.Feedbacks - a.Feedbacks),
  });

  // The submissions behind a number, as Error Agent Stats' side panel lists
  // them. onlyIds: just those submissions (the errors after coaching).
  const openSubs = async ({ title, section, question, agentUserId, kind = 'errors', onlyIds = null }) => {
    setPanel({ title, kind: 'subs', rows: [], loading: true });
    try {
      const res = await api.get('/api/errors/question-stats', {
        params: {
          ...buildParams(form.getFieldValue('range')), group_by: 'drill', drill_kind: kind, drill_section: section,
          ...(agentUserId != null ? { drill_agent_user_id: agentUserId } : {}),
          ...(question ? { drill_question: question } : {}),
        },
      });
      let rows = res.data || [];
      if (onlyIds) rows = rows.filter((r) => onlyIds.includes(r.ID));
      setPanel({ title, kind: 'subs', rows });
    } catch {
      setPanel({ title, kind: 'subs', rows: [] });
    }
  };

  const agentLink = (v, r) => (r.AgentUserID ? <Typography.Link onClick={() => openCheckup(r.AgentUserID)}>{v}</Typography.Link> : v);

  // -- tab 1: top error areas --------------------------------------------------
  const areaCols = (label) => [
    // Wide enough that no header wraps (the tables are full width); the
    // Section / Question column takes the rest and wraps.
    { title: 'Errors', dataIndex: 'Errors', key: 'Errors', width: 95, align: 'right', sorter: byNum('Errors') },
    ...(hasCalls ? [{ title: 'Per 100 Calls', dataIndex: 'Rate', key: 'Rate', width: 140, align: 'right', sorter: byNum('Rate'), render: rateCell }] : []),
    { title: 'Share', dataIndex: 'Share', key: 'Share', width: 90, align: 'right', sorter: byNum('Share'),
      render: (v) => (v == null ? dash : `${Math.round(v * 100)}%`) },
    { title: 'Change', key: 'chg', width: 90, align: 'right', render: (_, g) => change(g.Errors, g.Prev) },
    { title: 'Feedbacks', dataIndex: 'Feedbacks', key: 'Feedbacks', width: 125, align: 'right', sorter: byNum('Feedbacks') },
    { title: 'Agents', key: 'agents', width: 90, align: 'right', render: (_, g) => g.Spread?.agents ?? 0 },
    { title: 'Spread', key: 'spread', width: 130, align: 'center', render: (_, g) => spreadTag(g.Spread) },
  ].map((c) => ({ ...c, key: `${label}-${c.key}`, onHeaderCell: () => ({ style: { whiteSpace: 'nowrap' } }) }));

  const topTab = (
    <>
      <Space wrap size={24} style={{ marginBottom: 12 }}>
        <span><Typography.Text type="secondary">Errors: </Typography.Text><strong>{totalErrors}</strong></span>
        <span><Typography.Text type="secondary">Agents with errors: </Typography.Text><strong>{agentsWithErrors}</strong></span>
        {hasCalls && <span><Typography.Text type="secondary">Calls handled: </Typography.Text><strong>{calls.total_calls.toLocaleString()}</strong></span>}
        {hasCalls && (
          <span><Typography.Text type="secondary">Per 100 calls: </Typography.Text>
            <strong>{rateCell(groupRate({ agents: sections.flatMap((g) => g.agents) }))}</strong></span>
        )}
      </Space>
      <Row gutter={16}>
        <Col xs={24}>
          <Card size="small" title="Top Error Sections" style={{ marginBottom: 16 }}>
            <Table rowKey="key" size="small" loading={loading} dataSource={topSections} pagination={false}
              tableLayout="fixed"
              locale={{ emptyText: 'No errors for these filters.' }}
              onRow={(g) => ({ onClick: () => openAgents(`Agents on ${g.SectionName}`, g), style: { cursor: 'pointer' } })}
              columns={[
                { title: 'Section', dataIndex: 'SectionName', key: 'SectionName',
                  onCell: () => ({ style: { whiteSpace: 'normal' } }) },
                ...areaCols('s'),
              ]} />
          </Card>
        </Col>
        <Col xs={24}>
          <Card size="small" title="Top Error Questions" style={{ marginBottom: 16 }}>
            <Table rowKey="key" size="small" loading={loading} dataSource={topQuestions}
              pagination={{ defaultPageSize: 15, hideOnSinglePage: true }} tableLayout="fixed"
              locale={{ emptyText: 'No errors for these filters.' }}
              onRow={(g) => ({ onClick: () => openAgents(`Agents on ${g.QuestionText}`, g), style: { cursor: 'pointer' } })}
              columns={[
                { title: 'Question', dataIndex: 'QuestionText', key: 'QuestionText',
                  onCell: () => ({ style: { whiteSpace: 'normal' } }),
                  render: (v, g) => <div><div>{v}</div><div style={{ fontSize: 12, color: '#8c8c8c' }}>{g.SectionName}</div></div> },
                ...areaCols('q'),
              ]} />
          </Card>
        </Col>
      </Row>
    </>
  );

  // -- tab 2: repeat errors ----------------------------------------------------
  const secNames = topSections.map((g) => g.key);
  const grid = useMemo(() => {
    const m = {};
    qRows.forEach((r) => {
      const k = r.AgentUserID ?? `name:${r.AgentName}`;
      const a = (m[k] ||= { key: k, AgentUserID: r.AgentUserID, AgentName: r.AgentName, cells: {}, Errors: 0, Feedbacks: 0 });
      a.cells[r.SectionName] = (a.cells[r.SectionName] || 0) + (r.TotalErrors || 0);
      a.Errors    += r.TotalErrors || 0;
      a.Feedbacks += r.TotalFeedbacks || 0;
    });
    return Object.values(m);
  }, [qRows]);
  const gridRows = grid.map((a) => ({
    ...a,
    Rate: hasCalls ? per100(a.Errors, callsOf(a.AgentUserID)) : null,
    Repeats: Object.values(a.cells).filter((n) => n >= threshold).length,
  })).filter((a) => a.Errors > 0)
    .sort((a, b) => b.Repeats - a.Repeats || b.Errors - a.Errors || a.AgentName.localeCompare(b.AgentName));

  const afterCoaching = coaching.filter((r) => r.ErrorsAfterCoaching > 0)
    .sort((a, b) => b.ErrorsAfterCoaching - a.ErrorsAfterCoaching || a.AgentName.localeCompare(b.AgentName));
  const notCoached = coaching.filter((r) => r.Errors >= threshold && !r.Sessions.length)
    .sort((a, b) => b.Errors - a.Errors || a.AgentName.localeCompare(b.AgentName));

  const repeatTab = (
    <>
      <Card size="small" title={`Agents by Section (red = ${threshold}+ errors)`} style={{ marginBottom: 16 }}>
        <Table rowKey="key" size="small" loading={loading} dataSource={gridRows} pagination={{ defaultPageSize: 25, hideOnSinglePage: true }} bordered
          scroll={{ x: 'max-content' }}
          locale={{ emptyText: 'No errors for these filters.' }}
          columns={[
            { title: 'Agent', dataIndex: 'AgentName', key: 'AgentName', fixed: 'left', width: 190,
              sorter: (a, b) => a.AgentName.localeCompare(b.AgentName), render: agentLink },
            { title: 'Repeat Sections', dataIndex: 'Repeats', key: 'Repeats', width: 125, align: 'right', sorter: byNum('Repeats') },
            { title: 'Errors', dataIndex: 'Errors', key: 'Errors', width: 75, align: 'right', sorter: byNum('Errors') },
            ...(hasCalls ? [{ title: 'Per 100 Calls', dataIndex: 'Rate', key: 'Rate', width: 110, align: 'right', sorter: byNum('Rate'), render: rateCell }] : []),
            { title: 'Feedbacks', dataIndex: 'Feedbacks', key: 'Feedbacks', width: 90, align: 'right', sorter: byNum('Feedbacks') },
            ...secNames.map((sec) => ({
              title: sec, key: `sec-${sec}`, width: 120, align: 'center',
              sorter: (a, b) => (a.cells[sec] || 0) - (b.cells[sec] || 0),
              render: (_, a) => {
                const n = a.cells[sec] || 0;
                if (!n) return <span style={{ color: '#d9d9d9' }}>-</span>;
                return (
                  <span style={{ cursor: 'pointer' }}
                    onClick={() => openSubs({ title: `${a.AgentName}: ${sec}`, section: sec, agentUserId: a.AgentUserID })}>
                    {n >= threshold ? <Tag color="red" style={{ marginRight: 0 }}>{n}</Tag> : n}
                  </span>
                );
              },
            })),
          ]} />
      </Card>
      <Row gutter={16}>
        <Col xs={24} xl={13}>
          <Card size="small" title="Repeated After Coaching" style={{ marginBottom: 16 }}>
            <Table rowKey={(r) => `${r.AgentUserID}|${r.SectionName}`} size="small" loading={loading} dataSource={afterCoaching}
              pagination={{ defaultPageSize: 15, hideOnSinglePage: true }} scroll={{ x: 'max-content' }}
              locale={{ emptyText: 'No errors after coaching on the same section.' }}
              columns={[
                { title: 'Agent', dataIndex: 'AgentName', key: 'AgentName', width: 170, render: agentLink },
                { title: 'Section', dataIndex: 'SectionName', key: 'SectionName', width: 180, ellipsis: true },
                { title: 'Last Coached', dataIndex: 'LastCoached', key: 'LastCoached', width: 120,
                  render: (v, r) => {
                    const last = r.Sessions[r.Sessions.length - 1];
                    return last
                      ? <Typography.Link onClick={() => window.open(`/coaching/sessions/${last.ID}`, '_blank')}>{v}</Typography.Link>
                      : v;
                  } },
                { title: 'Sessions', key: 'Sessions', width: 80, align: 'right', render: (_, r) => r.Sessions.length },
                { title: 'Errors After', dataIndex: 'ErrorsAfterCoaching', key: 'ErrorsAfterCoaching', width: 100, align: 'center',
                  sorter: byNum('ErrorsAfterCoaching'),
                  render: (v, r) => (
                    <span style={{ cursor: 'pointer' }}
                      onClick={() => openSubs({ title: `${r.AgentName}: ${r.SectionName} (after coaching on ${r.LastCoached})`,
                        section: r.SectionName, agentUserId: r.AgentUserID, onlyIds: r.AfterSubmissionIDs })}>
                      {countTag(v)}
                    </span>
                  ) },
                { title: 'Errors', dataIndex: 'Errors', key: 'Errors', width: 70, align: 'right' },
              ]} />
          </Card>
        </Col>
        <Col xs={24} xl={11}>
          <Card size="small" title={`Repeat Errors, Not Coached (${threshold}+)`} style={{ marginBottom: 16 }}>
            <Table rowKey={(r) => `${r.AgentUserID}|${r.SectionName}`} size="small" loading={loading} dataSource={notCoached}
              pagination={{ defaultPageSize: 15, hideOnSinglePage: true }} scroll={{ x: 'max-content' }}
              locale={{ emptyText: 'No uncoached repeats.' }}
              onRow={(r) => ({ onClick: () => openSubs({ title: `${r.AgentName}: ${r.SectionName}`, section: r.SectionName, agentUserId: r.AgentUserID }),
                style: { cursor: 'pointer' } })}
              columns={[
                { title: 'Agent', dataIndex: 'AgentName', key: 'AgentName', width: 170, render: agentLink },
                { title: 'Section', dataIndex: 'SectionName', key: 'SectionName', width: 180, ellipsis: true },
                { title: 'Errors', dataIndex: 'Errors', key: 'Errors', width: 70, align: 'center', render: (v) => countTag(v) },
                { title: 'First', dataIndex: 'FirstError', key: 'FirstError', width: 105 },
                { title: 'Last', dataIndex: 'LastError', key: 'LastError', width: 105 },
              ]} />
          </Card>
        </Col>
      </Row>
    </>
  );

  // -- tab 3: agent check-up ---------------------------------------------------
  useEffect(() => {
    if (!agentId || !qRows.length) { setTrend([]); return; }
    api.get('/api/errors/question-stats', {
      params: { ...buildParams(form.getFieldValue('range')), group_by: 'month', agent_user_id: agentId },
    }).then((r) => setTrend(r.data || [])).catch(() => setTrend([]));
  }, [agentId, qRows]); // eslint-disable-line react-hooks/exhaustive-deps

  const agentCalls = callsOf(agentId);
  const agentErrors = qRows.filter((r) => r.AgentUserID === agentId).reduce((s, r) => s + (r.TotalErrors || 0), 0);
  // Without calls the comparison is the average per agent with any error or
  // feedback on these filters.
  const checkRows = (groups) => groups.map((g) => {
    const mine = g.agents.find((a) => a.AgentUserID === agentId);
    if (!mine) return null;
    const rate = hasCalls ? per100(mine.Errors, agentCalls) : null;
    const all  = hasCalls ? groupRate(g) : (grid.length ? Math.round((g.Errors / grid.length) * 100) / 100 : null);
    const val  = hasCalls ? rate : mine.Errors;
    const coach = coaching.find((c) => c.AgentUserID === agentId && c.SectionName === g.SectionName);
    return {
      key: g.key, SectionName: g.SectionName, QuestionText: g.QuestionText,
      Errors: mine.Errors, Feedbacks: mine.Feedbacks, Rate: rate, All: all,
      Gap: val != null && all != null ? Math.round((val - all) * 100) / 100 : null,
      Repeat: mine.Errors >= threshold, coach,
    };
  }).filter(Boolean).sort((a, b) => (b.Repeat - a.Repeat) || (b.Errors - a.Errors));
  const agentSecs = agentId ? checkRows(sections) : [];
  const agentQs   = agentId ? checkRows(questions) : [];
  const gapCell = (v) => (v == null ? '—' : <span style={{ color: v > 0 ? '#ff4d4f' : '#52c41a' }}>{v > 0 ? '+' : ''}{v}</span>);
  const compareCols = [
    { title: 'Errors', dataIndex: 'Errors', key: 'Errors', width: 70, align: 'center',
      render: (v, r) => (r.Repeat ? <Tag color="red" style={{ marginRight: 0 }}>{v}</Tag> : v) },
    ...(hasCalls
      ? [{ title: 'Per 100 Calls', dataIndex: 'Rate', key: 'Rate', width: 110, align: 'right', render: rateCell },
         { title: 'All Agents', dataIndex: 'All', key: 'All', width: 90, align: 'right', render: rateCell }]
      : [{ title: 'Avg / Agent', dataIndex: 'All', key: 'All', width: 95, align: 'right', render: rateCell }]),
    { title: 'Gap', dataIndex: 'Gap', key: 'Gap', width: 70, align: 'right', render: gapCell },
    { title: 'Feedbacks', dataIndex: 'Feedbacks', key: 'Feedbacks', width: 90, align: 'right' },
  ];

  const months = [...new Set(trend.map((r) => r.MonthKey))].sort();
  const trendRows = Object.values(trend.reduce((m, r) => {
    const s = (m[r.SectionName] ||= { key: r.SectionName, SectionName: r.SectionName });
    s[r.MonthKey] = (s[r.MonthKey] || 0) + (r.TotalErrors || 0);
    return m;
  }, {})).filter((s) => months.some((mk) => s[mk] > 0));

  const checkupTab = (
    <>
      <Select showSearch allowClear optionFilterProp="label" placeholder="Pick an agent" style={{ width: 320, marginBottom: 16 }}
        value={agentId} onChange={(v) => { setAgentId(v ?? null); setParams(v ? { agent: String(v) } : {}); }} options={agentOptions} />
      {!agentId ? <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Pick an agent." /> : (
        <>
          <Space wrap size={24} style={{ marginBottom: 12 }}>
            <span><Typography.Text type="secondary">Errors: </Typography.Text><strong>{agentErrors}</strong></span>
            {hasCalls && <span><Typography.Text type="secondary">Calls handled: </Typography.Text><strong>{agentCalls.toLocaleString()}</strong></span>}
            {hasCalls && (
              <span><Typography.Text type="secondary">Per 100 calls: </Typography.Text>
                <strong>{rateCell(per100(agentErrors, agentCalls))}</strong>
                <Typography.Text type="secondary"> (all agents {rateCell(groupRate({ agents: sections.flatMap((g) => g.agents) }))})</Typography.Text></span>
            )}
          </Space>
          <Row gutter={16}>
            <Col xs={24} xl={11}>
              <Card size="small" title="Sections" style={{ marginBottom: 16 }}>
                <Table rowKey="key" size="small" pagination={false} dataSource={agentSecs} scroll={{ x: 'max-content' }}
                  onRow={(r) => ({ onClick: () => openSubs({ title: `${agentOptions.find((o) => o.value === agentId)?.label}: ${r.SectionName}`,
                    section: r.SectionName, agentUserId: agentId }), style: { cursor: 'pointer' } })}
                  columns={[
                    { title: 'Section', dataIndex: 'SectionName', key: 'SectionName', width: 180, ellipsis: true },
                    ...compareCols,
                    { title: 'Coached', key: 'coach', width: 120,
                      render: (_, r) => (r.coach?.Sessions.length
                        ? <span>{r.coach.Sessions.length} · {r.coach.LastCoached}
                            {r.coach.ErrorsAfterCoaching > 0 && <Tag color="red" style={{ marginLeft: 6 }}>+{r.coach.ErrorsAfterCoaching} after</Tag>}</span>
                        : dash) },
                  ]} />
              </Card>
            </Col>
            <Col xs={24} xl={13}>
              <Card size="small" title="Questions" style={{ marginBottom: 16 }}>
                <Table rowKey="key" size="small" dataSource={agentQs} pagination={{ defaultPageSize: 15, hideOnSinglePage: true }} scroll={{ x: 'max-content' }}
                  onRow={(r) => ({ onClick: () => openSubs({ title: `${agentOptions.find((o) => o.value === agentId)?.label}: ${r.QuestionText}`,
                    section: r.SectionName, question: r.QuestionText, agentUserId: agentId }), style: { cursor: 'pointer' } })}
                  columns={[
                    { title: 'Question', dataIndex: 'QuestionText', key: 'QuestionText', width: 260,
                      onCell: () => ({ style: { whiteSpace: 'normal' } }),
                      render: (v, r) => <div><div>{v}</div><div style={{ fontSize: 12, color: '#8c8c8c' }}>{r.SectionName}</div></div> },
                    ...compareCols,
                  ]} />
              </Card>
            </Col>
          </Row>
          <Card size="small" title="Errors by Month">
            <Table rowKey="key" size="small" pagination={false} dataSource={trendRows} bordered scroll={{ x: 'max-content' }}
              locale={{ emptyText: 'No errors.' }}
              columns={[
                { title: 'Section', dataIndex: 'SectionName', key: 'SectionName', width: 200, fixed: 'left', ellipsis: true },
                ...months.map((mk) => ({
                  title: dayjs(`${mk}-01`).format('MMM YYYY'), key: mk, width: 100, align: 'center',
                  render: (_, r) => (r[mk] > 0 ? (r[mk] >= threshold ? <Tag color="red" style={{ marginRight: 0 }}>{r[mk]}</Tag> : r[mk])
                    : <span style={{ color: '#d9d9d9' }}>-</span>),
                })),
              ]} />
          </Card>
        </>
      )}
    </>
  );

  return (
    <div>
      {!embedded && <Typography.Title level={4} style={{ marginTop: 0 }}>Error Insights</Typography.Title>}

      <Form form={form} layout="inline" onFinish={load} style={{ marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
        <Form.Item name="range" label="Date of Error" hidden={embedded}><RangePicker style={{ width: 260 }} allowClear={false} /></Form.Item>
        <Form.Item name="campaigns" label="Campaign" hidden={embedded}
          rules={embedded ? [] : [{ required: true, message: 'Select a campaign' }]}>
          <Select mode="multiple" allowClear showSearch optionFilterProp="label" maxTagCount="responsive" placeholder="All campaigns" style={{ width: 220 }} options={campaigns} />
        </Form.Item>
        <Form.Item name="form_name" label="Error Form">
          <Select allowClear showSearch filterOption={selectFilter} placeholder="All forms" style={{ width: 220 }} options={forms} />
        </Form.Item>
        <Form.Item><Button type="primary" htmlType="submit" loading={loading}>Filter</Button></Form.Item>
      </Form>

      <Space wrap style={{ marginBottom: 8 }}>
        <span>Repeat at:</span>
        <InputNumber min={2} max={50} value={threshold} onChange={(v) => setThreshold(v ?? 4)} addonAfter="errors" style={{ width: 130 }} />
      </Space>

      <Tabs activeKey={tab} onChange={setTab} items={[
        { key: 'top',    label: 'Top Error Areas', children: topTab },
        { key: 'repeat', label: 'Repeat Errors',   children: repeatTab },
        { key: 'agent',  label: 'Agent Check-up',  children: checkupTab },
      ]} />

      <Drawer title={panel?.title} open={!!panel} onClose={() => setPanel(null)} width={780} destroyOnClose>
        {panel?.kind === 'agents' ? (
          <Table rowKey={(r) => r.AgentUserID ?? r.AgentName} size="small" pagination={false} dataSource={panel.rows}
            columns={[
              { title: 'Agent', dataIndex: 'AgentName', key: 'AgentName', render: agentLink },
              { title: 'Errors', dataIndex: 'Errors', key: 'Errors', width: 75, align: 'center',
                render: (v) => (v >= threshold ? <Tag color="red" style={{ marginRight: 0 }}>{v}</Tag> : v) },
              ...(hasCalls ? [{ title: 'Per 100 Calls', dataIndex: 'Rate', key: 'Rate', width: 110, align: 'right', render: rateCell }] : []),
              { title: 'Feedbacks', dataIndex: 'Feedbacks', key: 'Feedbacks', width: 90, align: 'right' },
            ]} />
        ) : (
          <Table rowKey="ID" size="small" loading={panel?.loading} pagination={{ defaultPageSize: 25, hideOnSinglePage: true }}
            dataSource={panel?.rows || []}
            locale={{ emptyText: 'No submissions.' }}
            // window.open, not a link: Chrome only copies the signed-in
            // session into a window a script opens.
            onRow={(r) => ({ onClick: () => window.open(`/errors/${r.ID}`, '_blank'), style: { cursor: 'pointer' } })}
            columns={[
              { title: 'ID', dataIndex: 'ID', key: 'ID', width: 70,
                render: (v) => <Typography.Text strong style={{ color: '#1677ff' }}>#{v}</Typography.Text> },
              { title: 'Date of Error', dataIndex: 'DateOfError', key: 'DateOfError', width: 115 },
              { title: 'Agent', dataIndex: 'AgentName', key: 'AgentName', width: 160 },
              { title: 'Campaign', dataIndex: 'Campaign', key: 'Campaign', width: 130, render: (v) => v || '—' },
              { title: 'Form', dataIndex: 'FormName', key: 'FormName', width: 200 },
              { title: 'Errors', dataIndex: 'Errors', key: 'Errors', width: 70, align: 'center', render: (v) => countTag(v) },
              { title: 'Feedbacks', dataIndex: 'Feedbacks', key: 'Feedbacks', width: 90, align: 'center', render: (v) => countTag(v, 'orange') },
            ]} />
        )}
      </Drawer>
    </div>
  );
}
